import { supabase } from '../lib/supabase';

export interface ChatbotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  actions?: ChatbotAction[];
}

export interface ChatbotAction {
  label: string;
  action: 'navigate' | 'action' | 'link';
  target: string;
  icon?: string;
}

export interface FAQ {
  question: string;
  answer: string;
  keywords: string[];
  category: string;
  actions?: ChatbotAction[];
}

export interface TroubleshootingStep {
  step: number;
  instruction: string;
  action?: ChatbotAction;
}

export interface ProblemSolution {
  problem: string;
  symptoms: string[];
  solution: string;
  steps: TroubleshootingStep[];
  keywords: string[];
}

// Dashboard-specific troubleshooting database
const troubleshootingDatabase: ProblemSolution[] = [
  {
    problem: 'Dashboard not loading data',
    symptoms: ['blank dashboard', 'loading forever', 'no data', 'empty dashboard', 'stuck loading'],
    solution: 'This is usually due to connectivity or cache issues. Let me help you fix it.',
    keywords: ['dashboard', 'loading', 'data', 'empty', 'blank', 'stuck', 'not showing'],
    steps: [
      { step: 1, instruction: 'First, refresh the page by pressing Ctrl+R (or Cmd+R on Mac)' },
      { step: 2, instruction: 'Check your internet connection - try opening another website' },
      { step: 3, instruction: 'Clear your browser cache: Settings → Privacy → Clear browsing data' },
      { step: 4, instruction: 'Try using an incognito/private browser window' },
      { step: 5, instruction: 'If still not working, log out and log back in', action: { label: 'Go to Settings', action: 'navigate', target: '/settings', icon: 'Settings' } }
    ]
  },
  {
    problem: 'Analytics not showing correctly',
    symptoms: ['wrong numbers', 'analytics error', 'graphs not loading', 'incorrect stats', 'metrics wrong'],
    solution: 'Analytics issues can be caused by date filters or data sync delays.',
    keywords: ['analytics', 'stats', 'metrics', 'numbers', 'wrong', 'graphs', 'charts'],
    steps: [
      { step: 1, instruction: 'Check the date range filter - make sure it\'s set correctly' },
      { step: 2, instruction: 'Analytics data syncs every 15 minutes - recent activity may take time to appear' },
      { step: 3, instruction: 'Click the refresh button in the analytics section' },
      { step: 4, instruction: 'If you see specific errors, note them and contact support' }
    ]
  },
  {
    problem: 'Campaign creation errors',
    symptoms: ['can\'t create campaign', 'campaign error', 'form not submitting', 'validation error'],
    solution: 'Campaign creation issues are often due to missing required fields or validation errors.',
    keywords: ['campaign', 'create', 'error', 'form', 'submit', 'validation', 'can\'t'],
    steps: [
      { step: 1, instruction: 'Ensure all required fields (marked with *) are filled in' },
      { step: 2, instruction: 'Check that your budget is within valid limits' },
      { step: 3, instruction: 'Make sure dates are valid (end date after start date)' },
      { step: 4, instruction: 'Verify that images/files meet the size requirements (max 5MB)' },
      { step: 5, instruction: 'Try saving as draft first, then publish', action: { label: 'Create Campaign', action: 'navigate', target: '/brand-dashboard/campaigns/create', icon: 'Plus' } }
    ]
  },
  {
    problem: 'Payment or withdrawal issues',
    symptoms: ['payment failed', 'can\'t withdraw', 'money not received', 'wallet error', 'transaction failed'],
    solution: 'Payment issues often relate to KYC verification or bank details.',
    keywords: ['payment', 'withdraw', 'money', 'wallet', 'transaction', 'bank', 'failed'],
    steps: [
      { step: 1, instruction: 'Check if your KYC verification is complete', action: { label: 'Check Verification', action: 'navigate', target: '/settings/verification', icon: 'Shield' } },
      { step: 2, instruction: 'Verify your bank account details are correct' },
      { step: 3, instruction: 'Minimum withdrawal amount is ₹500' },
      { step: 4, instruction: 'Bank transfers take 2-5 business days to process' },
      { step: 5, instruction: 'For failed transactions, wait 24-48 hours for auto-reversal' }
    ]
  },
  {
    problem: 'Profile or settings not saving',
    symptoms: ['changes not saving', 'profile update error', 'settings reset', 'lost changes'],
    solution: 'Save issues are often due to connection problems or validation errors.',
    keywords: ['save', 'profile', 'settings', 'update', 'changes', 'lost', 'not saving'],
    steps: [
      { step: 1, instruction: 'Ensure you click the "Save" button after making changes' },
      { step: 2, instruction: 'Check for any validation errors highlighted in red' },
      { step: 3, instruction: 'Make sure your session hasn\'t expired - try refreshing' },
      { step: 4, instruction: 'Try making changes one at a time to identify the issue' }
    ]
  },
  {
    problem: 'Messages not sending or receiving',
    symptoms: ['can\'t send message', 'messages not loading', 'chat not working', 'inbox empty'],
    solution: 'Messaging issues can be fixed with these steps.',
    keywords: ['message', 'chat', 'inbox', 'send', 'receive', 'conversation'],
    steps: [
      { step: 1, instruction: 'Refresh the messages page' },
      { step: 2, instruction: 'Check if you have a stable internet connection' },
      { step: 3, instruction: 'Ensure you\'re viewing the correct conversation' },
      { step: 4, instruction: 'Try sending a shorter message first', action: { label: 'Go to Messages', action: 'navigate', target: '/messages', icon: 'MessageCircle' } }
    ]
  },
  {
    problem: 'Application or proposal rejected',
    symptoms: ['application rejected', 'proposal denied', 'why rejected', 'application status'],
    solution: 'Understanding why applications get rejected can help you improve.',
    keywords: ['application', 'proposal', 'rejected', 'denied', 'status', 'why'],
    steps: [
      { step: 1, instruction: 'Check the campaign requirements - ensure your profile matches' },
      { step: 2, instruction: 'Complete your profile 100% with portfolio samples' },
      { step: 3, instruction: 'Personalize your proposals - generic ones often get rejected' },
      { step: 4, instruction: 'Ensure your social media metrics are up to date' },
      { step: 5, instruction: 'Keep trying! Brands often receive many applications' }
    ]
  },
  {
    problem: 'Account access issues',
    symptoms: ['locked out', 'can\'t access', 'account disabled', 'session expired'],
    solution: 'Account access issues can usually be resolved quickly.',
    keywords: ['account', 'access', 'locked', 'disabled', 'session', 'expired'],
    steps: [
      { step: 1, instruction: 'Try refreshing the page and logging in again' },
      { step: 2, instruction: 'Clear browser cookies for this site' },
      { step: 3, instruction: 'Use the "Forgot Password" option to reset access' },
      { step: 4, instruction: 'If account is disabled, contact support immediately', action: { label: 'Contact Support', action: 'link', target: 'mailto:hello@noonx.in', icon: 'Mail' } }
    ]
  }
];

// Comprehensive FAQ database for the platform
const faqDatabase: FAQ[] = [
  // Getting Started
  {
    category: 'Getting Started',
    question: 'How do I create an account?',
    answer: 'To create an account:\n1. Click "Sign up" on the authentication page\n2. Choose your role (Brand, Creator, or Agency)\n3. Fill in your details and verify your email\n4. Complete your profile setup\n\nYou can also sign up using Google or other social accounts for faster registration.',
    keywords: ['create', 'account', 'sign up', 'register', 'new', 'join']
  },
  {
    category: 'Getting Started',
    question: 'What types of accounts are available?',
    answer: 'NOONX offers three account types:\n\n• **Brand**: For businesses looking to collaborate with creators\n• **Creator**: For influencers and content creators\n• **Agency**: For marketing agencies managing multiple brands or creators\n\nEach account type has specific features tailored to your needs.',
    keywords: ['account', 'type', 'brand', 'creator', 'agency', 'role']
  },
  {
    category: 'Getting Started',
    question: 'Is NOONX free to use?',
    answer: 'Creating an account on NOONX is completely free! We offer:\n\n• Free basic features for all users\n• Premium subscription plans with advanced features\n• Campaign-specific fees for brands\n\nYou can explore the platform and its features before deciding on any paid services.',
    keywords: ['free', 'cost', 'price', 'pricing', 'subscription', 'pay', 'charge']
  },

  // Campaign Related
  {
    category: 'Campaigns',
    question: 'How do I create a campaign?',
    answer: 'To create a campaign:\n1. Go to your Brand Dashboard\n2. Click "Create Campaign" button\n3. Fill in campaign details (name, description, budget, requirements)\n4. Set target audience and creator criteria\n5. Review and publish your campaign\n\nCreators matching your criteria will be able to discover and apply for your campaign.',
    keywords: ['create', 'campaign', 'new campaign', 'start', 'launch']
  },
  {
    category: 'Campaigns',
    question: 'How do I apply for campaigns as a creator?',
    answer: 'To apply for campaigns:\n1. Browse available campaigns in your Creator Dashboard\n2. Use filters to find campaigns matching your niche\n3. Click on a campaign to view details\n4. Click "Apply" and submit your proposal\n5. Wait for the brand to review your application\n\nTip: Personalize your application to stand out!',
    keywords: ['apply', 'campaign', 'application', 'creator', 'proposal']
  },
  {
    category: 'Campaigns',
    question: 'How do I track my campaign performance?',
    answer: 'Track campaign performance through:\n\n• **Dashboard Analytics**: View real-time metrics\n• **Campaign Reports**: Detailed performance breakdown\n• **ROI Tracking**: Monitor return on investment\n• **Creator Performance**: Individual creator metrics\n\nAccess these from the Analytics section in your dashboard.',
    keywords: ['track', 'performance', 'analytics', 'metrics', 'report', 'roi']
  },

  // Payments
  {
    category: 'Payments',
    question: 'How do payments work?',
    answer: 'Our secure payment system:\n\n**For Brands:**\n• Add funds to your wallet\n• Pay creators upon deliverable approval\n• Track all transactions in Budget section\n\n**For Creators:**\n• Set up payment details in Settings\n• Receive payments after deliverable approval\n• Withdraw to your bank account\n\nAll payments are secured and encrypted.',
    keywords: ['payment', 'pay', 'money', 'transaction', 'wallet', 'fund']
  },
  {
    category: 'Payments',
    question: 'When will I get paid as a creator?',
    answer: 'Payment timeline for creators:\n\n1. Complete your deliverables\n2. Brand reviews and approves (1-3 days)\n3. Payment released to your wallet\n4. Withdraw to bank (2-5 business days)\n\nNote: Some campaigns may have milestone-based payments. Check campaign details for specifics.',
    keywords: ['paid', 'payment', 'when', 'earnings', 'withdraw', 'creator', 'money']
  },
  {
    category: 'Payments',
    question: 'How do I withdraw my earnings?',
    answer: 'To withdraw your earnings:\n1. Go to Settings > Payment Details\n2. Add your bank account (if not already added)\n3. Go to Earnings section\n4. Click "Withdraw"\n5. Enter amount and confirm\n\nMinimum withdrawal: ₹500\nProcessing time: 2-5 business days',
    keywords: ['withdraw', 'earnings', 'bank', 'transfer', 'cash out', 'payout']
  },

  // Technical Issues
  {
    category: 'Technical Issues',
    question: 'I can\'t log in to my account',
    answer: 'If you\'re having login issues:\n\n1. **Check your credentials**: Ensure email/password are correct\n2. **Reset password**: Click "Forgot password?" to reset\n3. **Clear browser cache**: Try clearing cookies and cache\n4. **Try incognito mode**: Use a private browser window\n5. **Check email verification**: Ensure your email is verified\n\nIf issues persist, contact support@noonx.in',
    keywords: ['login', 'log in', 'can\'t', 'access', 'password', 'error', 'problem']
  },
  {
    category: 'Technical Issues',
    question: 'The page is loading slowly or not working',
    answer: 'For performance issues:\n\n1. **Refresh the page**: Press Ctrl+R or Cmd+R\n2. **Check internet connection**: Ensure stable connection\n3. **Clear browser cache**: Remove cached data\n4. **Try another browser**: Chrome/Firefox recommended\n5. **Disable extensions**: Some extensions may interfere\n\nIf the issue continues, please report it to us.',
    keywords: ['slow', 'loading', 'not working', 'crash', 'error', 'page', 'freeze']
  },
  {
    category: 'Technical Issues',
    question: 'How do I reset my password?',
    answer: 'To reset your password:\n\n1. Go to the login page\n2. Click "Forgot password?"\n3. Enter your registered email\n4. Check your email for reset link\n5. Click the link and set new password\n\nNote: Reset links expire after 24 hours. If you don\'t see the email, check spam folder.',
    keywords: ['reset', 'password', 'forgot', 'change', 'new password']
  },

  // Profile & Settings
  {
    category: 'Profile & Settings',
    question: 'How do I update my profile?',
    answer: 'To update your profile:\n\n1. Click on your avatar in the top right\n2. Select "Settings" or "Profile"\n3. Update your information:\n   • Profile picture\n   • Bio/Description\n   • Contact details\n   • Social media links\n4. Click "Save Changes"\n\nA complete profile increases your visibility!',
    keywords: ['update', 'profile', 'edit', 'change', 'settings', 'information']
  },
  {
    category: 'Profile & Settings',
    question: 'How do I complete KYC verification?',
    answer: 'To complete KYC verification:\n\n1. Go to Settings > Verification\n2. Upload required documents:\n   • Government ID (Aadhaar/PAN/Passport)\n   • Selfie for verification\n   • Bank statement (for payment verification)\n3. Submit for review\n4. Verification takes 1-3 business days\n\nKYC is required for payment withdrawals.',
    keywords: ['kyc', 'verification', 'verify', 'document', 'id', 'identity']
  },

  // Messaging
  {
    category: 'Messaging',
    question: 'How do I message a brand or creator?',
    answer: 'To send messages:\n\n1. Go to the user\'s profile or campaign page\n2. Click "Message" or "Contact"\n3. Type your message and send\n4. View all conversations in your Inbox\n\nYou can also access messages from the Messages icon in the top navigation.',
    keywords: ['message', 'chat', 'contact', 'communicate', 'talk', 'inbox']
  },

  // CRM & Analytics
  {
    category: 'CRM & Analytics',
    question: 'What is the CRM feature?',
    answer: 'The CRM (Customer Relationship Management) feature helps brands:\n\n• **Manage creator relationships**: Track all interactions\n• **View collaboration history**: See past campaigns\n• **Score creators**: Rate and categorize creators\n• **Notes & tags**: Add custom notes and labels\n• **Pipeline management**: Track outreach stages\n\nAccess CRM from your Brand Dashboard.',
    keywords: ['crm', 'relationship', 'management', 'creator', 'track', 'organize']
  },
  {
    category: 'CRM & Analytics',
    question: 'How do I view my analytics?',
    answer: 'Access analytics from:\n\n**Dashboard Overview:**\n• Quick stats and metrics\n• Performance trends\n\n**Analytics Section:**\n• Detailed reports\n• Campaign comparisons\n• ROI calculations\n• Export options\n\nUse filters to customize date ranges and data views.',
    keywords: ['analytics', 'data', 'report', 'metrics', 'statistics', 'insights']
  },

  // Support
  {
    category: 'Support',
    question: 'How do I contact support?',
    answer: 'Contact NOONX support through:\n\n• **Email**: hello@noonx.in\n• **Chat**: Use this chatbot for quick help\n• **Help Center**: Visit /help for guides\n• **Social Media**: @noonx on Instagram/Twitter\n\nSupport hours: Mon-Fri, 9 AM - 6 PM IST\nResponse time: Usually within 24 hours.',
    keywords: ['support', 'help', 'contact', 'email', 'customer service', 'issue']
  },
  {
    category: 'Support',
    question: 'I have a dispute with a brand/creator',
    answer: 'To resolve disputes:\n\n1. First, try communicating directly through messages\n2. If unresolved, go to Settings > Report Issue\n3. Select the campaign/user involved\n4. Describe the issue in detail\n5. Our team will review and mediate\n\nWe aim to resolve disputes within 5-7 business days.',
    keywords: ['dispute', 'problem', 'issue', 'complaint', 'conflict', 'report']
  }
];

// Smart keyword matching with scoring
function findBestMatch(userInput: string): FAQ | null {
  const input = userInput.toLowerCase().trim();
  let bestMatch: FAQ | null = null;
  let highestScore = 0;

  for (const faq of faqDatabase) {
    let score = 0;
    
    // Check keyword matches
    for (const keyword of faq.keywords) {
      if (input.includes(keyword.toLowerCase())) {
        score += 2;
      }
    }
    
    // Check question similarity
    const questionWords = faq.question.toLowerCase().split(' ');
    for (const word of questionWords) {
      if (input.includes(word) && word.length > 3) {
        score += 1;
      }
    }
    
    // Bonus for exact keyword match
    for (const keyword of faq.keywords) {
      if (input === keyword.toLowerCase()) {
        score += 5;
      }
    }
    
    if (score > highestScore) {
      highestScore = score;
      bestMatch = faq;
    }
  }

  // Return match only if score is above threshold
  return highestScore >= 2 ? bestMatch : null;
}

// Find troubleshooting solution based on user input
function findTroubleshootingSolution(userInput: string): ProblemSolution | null {
  const input = userInput.toLowerCase().trim();
  let bestMatch: ProblemSolution | null = null;
  let highestScore = 0;

  for (const problem of troubleshootingDatabase) {
    let score = 0;
    
    // Check keyword matches
    for (const keyword of problem.keywords) {
      if (input.includes(keyword.toLowerCase())) {
        score += 3;
      }
    }
    
    // Check symptom matches
    for (const symptom of problem.symptoms) {
      if (input.includes(symptom.toLowerCase())) {
        score += 4;
      }
    }
    
    // Check problem name similarity
    const problemWords = problem.problem.toLowerCase().split(' ');
    for (const word of problemWords) {
      if (input.includes(word) && word.length > 3) {
        score += 1;
      }
    }
    
    if (score > highestScore) {
      highestScore = score;
      bestMatch = problem;
    }
  }

  return highestScore >= 3 ? bestMatch : null;
}

// Format troubleshooting steps as readable text
function formatTroubleshootingResponse(solution: ProblemSolution): { content: string; actions: ChatbotAction[] } {
  let content = `🔧 **${solution.problem}**\n\n`;
  content += `${solution.solution}\n\n`;
  content += `**Here's how to fix it:**\n\n`;
  
  const actions: ChatbotAction[] = [];
  
  for (const step of solution.steps) {
    content += `${step.step}. ${step.instruction}\n`;
    if (step.action) {
      actions.push(step.action);
    }
  }
  
  content += `\n💡 **Still having issues?** Contact our support team and we'll help you resolve this quickly!`;
  
  return { content, actions };
}

// Get suggested questions based on category or context
function getSuggestions(currentTopic?: string): string[] {
  const suggestions: string[] = [];
  
  if (currentTopic) {
    const categoryFaqs = faqDatabase.filter(f => 
      f.category.toLowerCase() === currentTopic.toLowerCase()
    );
    categoryFaqs.slice(0, 3).forEach(f => suggestions.push(f.question));
  }
  
  if (suggestions.length < 3) {
    const popular = [
      'How do I create a campaign?',
      'How do payments work?',
      'How do I contact support?'
    ];
    popular.forEach(q => {
      if (!suggestions.includes(q)) suggestions.push(q);
    });
  }
  
  return suggestions.slice(0, 3);
}

// Get problem-solving suggestions
function getProblemSuggestions(): string[] {
  return [
    'Dashboard not loading',
    'Payment issues',
    'Campaign creation help',
    'Profile not saving'
  ];
}

// Generate greeting based on time and context
function generateGreeting(userName?: string, userRole?: string): string {
  const hour = new Date().getHours();
  let timeGreeting = 'Hello';
  
  if (hour < 12) timeGreeting = 'Good morning';
  else if (hour < 17) timeGreeting = 'Good afternoon';
  else timeGreeting = 'Good evening';
  
  let greeting = `${timeGreeting}${userName ? `, ${userName}` : ''}! 👋\n\n`;
  greeting += `I'm your NOONX assistant. I'm here to help you with:\n\n`;
  greeting += `• Creating and managing campaigns\n`;
  greeting += `• Payment and withdrawal questions\n`;
  greeting += `• Technical issues and troubleshooting\n`;
  greeting += `• Profile and account settings\n`;
  greeting += `• And much more!\n\n`;
  greeting += `How can I help you today?`;
  
  return greeting;
}

export const chatbotService = {
  // Get initial greeting
  getGreeting(userName?: string, userRole?: string): ChatbotMessage {
    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: generateGreeting(userName, userRole),
      timestamp: new Date(),
      suggestions: getSuggestions(),
      actions: [
        { label: 'Report a Problem', action: 'action', target: 'showProblems', icon: 'AlertCircle' },
        { label: 'View FAQs', action: 'action', target: 'showFAQs', icon: 'HelpCircle' }
      ]
    };
  },

  // Process user message and generate response
  processMessage(userInput: string, userRole?: string): ChatbotMessage {
    const input = userInput.trim();
    
    // Handle greetings
    const greetings = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening'];
    if (greetings.some(g => input.toLowerCase().startsWith(g))) {
      return {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: 'Hello! 👋 How can I assist you today? Feel free to ask me anything about NOONX or describe any problems you\'re facing.',
        timestamp: new Date(),
        suggestions: getSuggestions(),
        actions: [
          { label: 'I have a problem', action: 'action', target: 'showProblems', icon: 'AlertCircle' },
          { label: 'Quick Help', action: 'action', target: 'showFAQs', icon: 'Zap' }
        ]
      };
    }
    
    // Handle thanks
    const thanks = ['thank', 'thanks', 'appreciate', 'helpful'];
    if (thanks.some(t => input.toLowerCase().includes(t))) {
      return {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: 'You\'re welcome! 😊 Is there anything else I can help you with?',
        timestamp: new Date(),
        suggestions: getSuggestions()
      };
    }

    // Handle problem/issue keywords - prioritize troubleshooting
    const problemKeywords = ['problem', 'issue', 'error', 'not working', 'broken', 'help', 'fix', 'stuck', 'can\'t', 'cannot', 'failed', 'failing'];
    const isProblemReport = problemKeywords.some(k => input.toLowerCase().includes(k));
    
    // First, check for troubleshooting matches if it seems like a problem
    if (isProblemReport) {
      const troubleshootMatch = findTroubleshootingSolution(input);
      if (troubleshootMatch) {
        const { content, actions } = formatTroubleshootingResponse(troubleshootMatch);
        return {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content,
          timestamp: new Date(),
          suggestions: getProblemSuggestions(),
          actions: actions.length > 0 ? actions : undefined
        };
      }
    }
    
    // Find matching FAQ
    const match = findBestMatch(input);
    
    if (match) {
      return {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: match.answer,
        timestamp: new Date(),
        suggestions: getSuggestions(match.category),
        actions: match.actions
      };
    }

    // Check troubleshooting even if not explicitly a problem report
    const troubleshootMatch = findTroubleshootingSolution(input);
    if (troubleshootMatch) {
      const { content, actions } = formatTroubleshootingResponse(troubleshootMatch);
      return {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        suggestions: getProblemSuggestions(),
        actions: actions.length > 0 ? actions : undefined
      };
    }
    
    // No match found - provide helpful response
    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `I'm not sure I understand that completely. Let me help you find what you need:\n\n**Common Topics:**\n• Campaign creation and management\n• Payment and earnings questions\n• Account and profile settings\n• Technical troubleshooting\n\n**Describe your issue:**\nTry telling me specifically what's happening, like "my dashboard isn't loading" or "I can't create a campaign".\n\nOr contact our support team at **hello@noonx.in** for personalized assistance.`,
      timestamp: new Date(),
      suggestions: [
        'Dashboard not loading',
        'How do payments work?',
        'Contact support'
      ],
      actions: [
        { label: 'Common Problems', action: 'action', target: 'showProblems', icon: 'AlertCircle' },
        { label: 'Email Support', action: 'link', target: 'mailto:hello@noonx.in', icon: 'Mail' }
      ]
    };
  },

  // Get all FAQ categories
  getCategories(): string[] {
    return [...new Set(faqDatabase.map(f => f.category))];
  },

  // Get FAQs by category
  getFaqsByCategory(category: string): FAQ[] {
    return faqDatabase.filter(f => f.category === category);
  },

  // Get all troubleshooting problems
  getTroubleshootingProblems(): ProblemSolution[] {
    return troubleshootingDatabase;
  },

  // Search FAQs
  searchFaqs(query: string): FAQ[] {
    const input = query.toLowerCase();
    return faqDatabase.filter(faq => 
      faq.question.toLowerCase().includes(input) ||
      faq.keywords.some(k => k.toLowerCase().includes(input))
    );
  },

  // Get specific troubleshooting solution
  getTroubleshootingSolution(problemId: string): ProblemSolution | undefined {
    return troubleshootingDatabase.find(p => 
      p.problem.toLowerCase().replace(/\s+/g, '-') === problemId.toLowerCase()
    );
  },

  // Save conversation to database (optional)
  async saveConversation(userId: string, messages: ChatbotMessage[]): Promise<void> {
    try {
      await supabase
        .from('chatbot_conversations')
        .insert({
          user_id: userId,
          messages: messages,
          created_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Failed to save chatbot conversation:', error);
    }
  },

  // Get conversation history
  async getConversationHistory(userId: string, limit = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('chatbot_conversations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get conversation history:', error);
      return [];
    }
  }
};
